// Lawyer side of the video call, mounted once in the portal layout so a call
// can ring over any page. Mirrors the Partner portal's incoming-call.blade.php:
//
//   incoming-call / incoming-consultation  → ring, with the client's case details
//   Accept → camera/mic check → claim over REST → call-accept / consultation-accept
//          → join the Agora channel (LawyerCallScreen)
//   Decline (direct call) → call-reject + REST reject
//   consultation-taken / consultation-cancelled / call-cancelled → stop ringing
//   call-held / call-resumed → the client stepped away (handled in the call screen)
//   call-ended → report it over REST and tear down
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import socketService from "../../../services/SocketService";
import { apiErrorMessage, lawyerService } from "../../../services/lawyerApi";
import { ensureMediaPermission, getMediaPermissionState } from "../../call/media";
import MediaPermissionModal from "../../call/MediaPermissionModal";
import { Avatar } from "../LawyerUi";
import LawyerCallScreen from "./LawyerCallScreen";
import { createRinger } from "./ringer";

// Local fallback for a ring nobody resolves. The client's own ring gives up
// sooner and sends a cancel; this only covers a cancel that never arrives.
const RING_TIMEOUT_MS = 60 * 1000;

const sameCall = (a, payload) => !!a && (!payload?.callId || String(payload.callId) === String(a.callId));

const CaseRow = ({ label, value }) =>
  value ? (
    <div className="lwp-ring-case-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  ) : null;

const LawyerCallManager = ({ profile }) => {
  const [ring, setRing] = useState(null); // { callId, channel, user, broadcast, durationMins, caseDetails }
  const [call, setCall] = useState(null); // { callId, creds, user, caseDetails, durationMinutes }
  const [accepting, setAccepting] = useState(false);
  const [mediaPrompt, setMediaPrompt] = useState(null);

  const navigate = useNavigate();
  const ringRef = useRef(null);
  const callRef = useRef(null);
  const ringer = useRef(null);
  const ringTimer = useRef(null);
  const titleRef = useRef(document.title);
  ringRef.current = ring;
  callRef.current = call;

  const lawyerId = profile?.id;
  const lawyerName = profile?.name;
  const lawyerPicture = profile?.picture;

  if (!ringer.current) ringer.current = createRinger(`${process.env.PUBLIC_URL || ""}/sounds/ringtone.mp3`);

  const stopRinging = useCallback(() => {
    clearTimeout(ringTimer.current);
    ringer.current.stop();
    document.title = titleRef.current;
    setMediaPrompt(null);
    setRing(null);
  }, []);

  /* ----- ringing ----- */

  const startRinging = useCallback(
    (data, broadcast) => {
      if (!data?.callId) return;
      // Already on a call or ringing: a broadcast just passes us by; a direct
      // call is turned down so the client isn't left waiting on us.
      if (callRef.current || ringRef.current) {
        if (!broadcast && !sameCall(ringRef.current, data)) {
          socketService.emit("call-reject", { callId: data.callId, user_id: data.user?.id });
          lawyerService.rejectCall(data.callId).catch(() => {});
        }
        return;
      }

      const next = {
        callId: data.callId,
        channel: data.channel,
        user: data.user || {},
        broadcast,
        durationMins: Number(data.durationMins) || 0,
        caseDetails: null,
      };
      setRing(next);
      ringer.current.start();
      titleRef.current = document.title;
      document.title = "📞 Incoming call · Legal Platform";
      clearTimeout(ringTimer.current);
      ringTimer.current = setTimeout(() => {
        if (sameCall(ringRef.current, next)) {
          stopRinging();
          toast.info("You missed a call.");
        }
      }, RING_TIMEOUT_MS);

      // Put the browser's camera prompt up while it rings, so it is settled by
      // the time they press Accept. Not awaited — the ring must not wait on it.
      ensureMediaPermission().catch(() => {});

      // The socket payload doesn't carry the case — fetch what the client wrote.
      lawyerService
        .getCallCaseDetails(data.callId)
        .then((res) => {
          const cd = res?.data?.data?.caseDetails;
          if (cd && sameCall(ringRef.current, next)) setRing((r) => (r ? { ...r, caseDetails: cd } : r));
        })
        .catch((err) => {
          // 409: another lawyer already took this consultation.
          if (err?.response?.status === 409 && sameCall(ringRef.current, next)) {
            stopRinging();
          }
        });
    },
    [stopRinging]
  );

  /* ----- ending ----- */

  const finishCall = useCallback(
    async (reason) => {
      const current = callRef.current;
      if (!current) return;
      setCall(null);
      if (reason === "remote") toast.info("The client ended the call.");
      else if (reason === "timeout") toast.info("The consultation time is up. The call has ended.");
      else if (reason === "left") toast.info("The client left the call.");

      // Both sides report the end; the first one settles the payment and the
      // rest are no-ops. Ours matters when the client's app is already gone.
      // Awaited so the call record is in the chat by the time we open it.
      await lawyerService.endCall(current.callId).catch(() => {});

      // Carry on with the client in chat, where the call record now sits.
      const userId = current.user?.id;
      if (!userId) return;
      try {
        const res = await lawyerService.getCallChat(userId);
        const chatId = res?.data?.data?.chat?.id;
        if (chatId) navigate(`/lawyer/chat/${chatId}`, { state: { fromCall: Date.now() } });
      } catch {
        // Couldn't resolve the chat — stay where they are.
      }
    },
    [navigate]
  );

  // Called by the call screen: plan ran out, or the client dropped out of the channel.
  const endFromScreen = useCallback(
    (reason) => {
      const current = callRef.current;
      if (!current) return;
      socketService.emit("call-end", { callId: current.callId });
      finishCall(reason);
    },
    [finishCall]
  );

  /* ----- socket events ----- */

  useEffect(() => {
    if (!lawyerId) return undefined;
    const offs = [
      socketService.subscribe("incoming-call", (data) => startRinging(data, false)),
      socketService.subscribe("incoming-consultation", (data) => startRinging(data, true)),
      socketService.subscribe("consultation-taken", (p) => {
        if (sameCall(ringRef.current, p)) {
          stopRinging();
          toast.info("Another lawyer took this consultation.");
        }
      }),
      socketService.subscribe("consultation-cancelled", (p) => {
        if (sameCall(ringRef.current, p)) {
          stopRinging();
          toast.info("The client cancelled the consultation request.");
        }
      }),
      socketService.subscribe("call-cancelled", (p) => {
        if (sameCall(ringRef.current, p)) {
          stopRinging();
          toast.info("The client cancelled the call.");
        }
      }),
      socketService.subscribe("call-ended", (p) => {
        if (sameCall(callRef.current, p)) finishCall("remote");
        else if (sameCall(ringRef.current, p)) stopRinging();
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [lawyerId, startRinging, stopRinging, finishCall]);

  useEffect(
    () => () => {
      clearTimeout(ringTimer.current);
      ringer.current?.stop();
    },
    []
  );

  /* ----- accept / decline ----- */

  const accept = async () => {
    const current = ringRef.current;
    if (!current || accepting) return;

    // Nothing is claimed until the camera and mic are ours. Claiming a broadcast
    // consultation takes it from every other lawyer, so a blocked camera has to
    // stop us here while the call can still go to someone who can take it.
    const media = await ensureMediaPermission();
    if (!media.ok) {
      setMediaPrompt({ reason: media.reason, state: await getMediaPermissionState() });
      return;
    }
    setMediaPrompt(null);

    setAccepting(true);
    ringer.current.stop();
    try {
      const res = current.broadcast
        ? await lawyerService.acceptConsultation(current.callId)
        : await lawyerService.acceptCall(current.callId);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const d = res.data.data;

      // The ring was called off while we were claiming it — release the claim.
      if (!sameCall(ringRef.current, current)) {
        lawyerService.endCall(current.callId).catch(() => {});
        return;
      }

      const userId = current.user?.id || d.user_id;
      socketService.emit(current.broadcast ? "consultation-accept" : "call-accept", {
        callId: current.callId,
        lawyer_id: lawyerId,
        user_id: userId,
        lawyer: { id: lawyerId, name: lawyerName, picture: lawyerPicture },
      });

      const caseDetails = d.caseDetails || current.caseDetails || {};
      stopRinging();
      setCall({
        callId: current.callId,
        creds: { appId: d.agoraAppId, channel: d.channel, token: d.token, uid: d.uid },
        user: { ...current.user, id: userId },
        caseDetails,
        durationMinutes: Number(caseDetails.duration_minutes) || current.durationMins || 0,
      });
    } catch (err) {
      stopRinging();
      toast.error(
        err?.response?.status === 409
          ? apiErrorMessage(err, "This consultation was already taken by another lawyer.")
          : apiErrorMessage(err, err?.message || "Could not start the call.")
      );
    } finally {
      setAccepting(false);
    }
  };

  const decline = () => {
    const current = ringRef.current;
    if (!current) return;
    stopRinging();
    // A broadcast is ringing other lawyers too: declining only dismisses it
    // here. Emitting call-reject would tell the client "rejected" and drop the
    // request for everyone.
    if (current.broadcast) return;
    socketService.emit("call-reject", { callId: current.callId, user_id: current.user?.id });
    lawyerService.rejectCall(current.callId).catch(() => {});
  };

  /* ----- render ----- */

  if (call) {
    return <LawyerCallScreen call={call} onEnd={endFromScreen} />;
  }

  if (!ring) return null;

  const cd = ring.caseDetails || {};
  const plan = cd.plan || (ring.durationMins ? `${ring.durationMins} Minutes` : "");

  return (
    <div className="lwp lwp-ring-root" role="dialog" aria-modal="true" aria-label="Incoming call">
      <div className="lwp-ring">
        <span className="lwp-ring-kind">
          <i className="bi bi-camera-video-fill" /> {ring.broadcast ? "New consultation request" : "Incoming video call"}
        </span>

        <div className="lwp-ring-avatar">
          <span className="lwp-ring-pulse" />
          <span className="lwp-ring-pulse delay" />
          <Avatar src={ring.user?.picture} alt={ring.user?.name} size={104} />
        </div>
        <h2 className="lwp-ring-name">{ring.user?.name || "Client"}</h2>
        <p className="lwp-ring-sub">is calling you…</p>

        <div className="lwp-ring-case">
          {ring.caseDetails ? (
            <>
              <CaseRow label="Category" value={cd.category} />
              <CaseRow label="Jurisdiction" value={cd.jurisdiction} />
              <CaseRow label="Plan" value={plan} />
              {cd.description && <p className="lwp-ring-desc">{cd.description}</p>}
            </>
          ) : (
            <p className="lwp-ring-loading">
              <span className="spinner-border spinner-border-sm" /> Loading case details…
            </p>
          )}
        </div>

        <div className="lwp-ring-actions">
          <div className="lwp-ring-action">
            <button type="button" className="lwp-ring-btn decline" onClick={decline} disabled={accepting} aria-label="Decline">
              <i className="bi bi-telephone-x-fill" />
            </button>
            <span>Decline</span>
          </div>
          <div className="lwp-ring-action">
            <button type="button" className="lwp-ring-btn accept" onClick={accept} disabled={accepting} aria-label="Accept">
              {accepting ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-telephone-fill" />}
            </button>
            <span>{accepting ? "Connecting…" : "Accept"}</span>
          </div>
        </div>
      </div>

      {mediaPrompt && (
        <MediaPermissionModal
          reason={mediaPrompt.reason}
          state={mediaPrompt.state}
          onRetry={async () => {
            const media = await ensureMediaPermission();
            if (media.ok) {
              setMediaPrompt(null);
              return true;
            }
            setMediaPrompt({ reason: media.reason, state: await getMediaPermissionState() });
            return false;
          }}
          onCancel={() => setMediaPrompt(null)}
        />
      )}
    </div>
  );
};

export default LawyerCallManager;
